import React, { useMemo } from "react";
import { motion } from "framer-motion";
import {
    fadeUpFromBottom,
    staggerContainer,
    useWdInView,
    wdViewportEarly,
} from "./WdMotion";
import { useCountUp } from "../../hooks/useCountUp";
import {
    wdEcommerceProducts,
} from "../../data/websiteDevelopmentPageContent";

export const WdEcommerceSection = () => {
    const { ref: sectionRef, inView } = useWdInView({ amount: 0.1 });
    const revenue = useCountUp(248500, 2200, inView);
    const cartCount = useCountUp(12, 1400, inView);
    const marqueeProducts = useMemo(
        () => [...wdEcommerceProducts, ...wdEcommerceProducts],
        []
    );

    const formattedRevenue = new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: "INR",
        maximumFractionDigits: 0,
    }).format(revenue);

    return (
        <section className="wd-section wd-ecom" ref={sectionRef}>
            <div className="wd-container wd-ecom__grid">
                <motion.div
                    className="wd-section-header wd-ecom__heading-group"
                    initial="hidden"
                    whileInView="visible"
                    viewport={wdViewportEarly}
                    variants={fadeUpFromBottom}
                >
                    <h2 className="wd-section-title">Ecommerce Website</h2>
                    <p className="wd-section-subtitle">
                        Stores built to <span className="wd-accent">convert</span>
                    </p>
                </motion.div>

                <div className="wd-ecom__visual">
                    <div className="wd-ecom__stats">
                        <div className="wd-ecom__stat">
                            <span className="wd-ecom__stat-label">Monthly Revenue</span>
                            <strong className="wd-ecom__stat-value">{formattedRevenue}</strong>
                        </div>
                        <div className="wd-ecom__stat wd-ecom__stat--cart">
                            <span className="wd-ecom__stat-label">Cart Items</span>
                            <strong className="wd-ecom__stat-value">{cartCount}</strong>
                            <span className="wd-ecom__cart-icon" aria-hidden>
                                🛒
                            </span>
                        </div>
                    </div>

                    <div className="wd-ecom__slider-wrap" aria-label="Product showcase">
                        <div className="wd-ecom__slider-fade wd-ecom__slider-fade--left" aria-hidden="true" />
                        <div className="wd-ecom__slider-fade wd-ecom__slider-fade--right" aria-hidden="true" />
                        <motion.div
                            className="wd-ecom__slider-viewport"
                            initial="hidden"
                            whileInView="visible"
                            viewport={wdViewportEarly}
                            variants={staggerContainer}
                        >
                            <div className="wd-ecom__slider">
                                {marqueeProducts.map((product, index) => (
                                    <motion.article
                                        key={`${product.name}-${index}`}
                                        className="wd-ecom__product"
                                        variants={fadeUpFromBottom}
                                        custom={(index % wdEcommerceProducts.length) * 0.05}
                                    >
                                        <img
                                            src={product.image}
                                            alt={product.imageAlt}
                                            className="wd-ecom__product-img"
                                            loading="lazy"
                                            decoding="async"
                                        />
                                        <div className="wd-ecom__product-name">{product.name}</div>
                                        <div className="wd-ecom__product-meta">
                                            <span>{product.price}</span>
                                            <span
                                                className={`wd-ecom__stock${
                                                    product.stock === "Low Stock"
                                                        ? " wd-ecom__stock--low"
                                                        : ""
                                                }`}
                                            >
                                                {product.stock}
                                            </span>
                                        </div>
                                        <button type="button" className="wd-ecom__add-btn">
                                            Add to Cart
                                        </button>
                                    </motion.article>
                                ))}
                            </div>
                        </motion.div>
                    </div>
                </div>

                <motion.p
                    className="wd-section-desc wd-ecom__desc"
                    initial="hidden"
                    whileInView="visible"
                    viewport={wdViewportEarly}
                    variants={fadeUpFromBottom}
                    custom={0.06}
                >
                    Build powerful online stores with product catalogs, secure payment
                    gateways, inventory management, order tracking, and customer engagement
                    tools designed to increase conversions and sales.
                </motion.p>
            </div>
        </section>
    );
};
